import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Descriptions,
  Avatar,
  Switch,
  message,
  Spin,
  Form,
  Input,
  Space,
  Statistic,
  Alert,
  Typography,
  Divider,
} from "antd";

import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  TwitterOutlined,
  FacebookOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DollarOutlined,
  BankOutlined,
  StockOutlined,
  GiftOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import { getUserProfile, updateUserProfile, updateUserInfo } from "../services/profileService";
import BgProfile from "../assets/images/bg-profile.jpg";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function Profile() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      const data = await getUserProfile();
      setProfileData(data);
      
      // Set form initial values
      if (data.profile) {
        form.setFieldsValue({
          first_name: data.user.first_name || '',
          last_name: data.user.last_name || '',
          bio: data.profile.bio || '',
          phone: data.profile.phone || '',
          location: data.profile.location || '',
          gravatar_email: data.profile.gravatar_email || '',
          twitter_handle: data.profile.twitter_handle || '',
          facebook_url: data.profile.facebook_url || '',
          instagram_handle: data.profile.instagram_handle || '',
          linkedin_url: data.profile.linkedin_url || '',
        });
      }
    } catch (error) {
      console.error('프로필 로드 실패:', error);
      message.error(error.message || '프로필을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (values) => {
    try {
      setUpdating(true);

      // Update user info (first_name, last_name)
      if (values.first_name !== undefined || values.last_name !== undefined) {
        await updateUserInfo({
          first_name: values.first_name,
          last_name: values.last_name,
        });
      }

      // Update profile info
      await updateUserProfile({
        bio: values.bio,
        phone: values.phone,
        location: values.location,
        gravatar_email: values.gravatar_email,
        twitter_handle: values.twitter_handle,
        facebook_url: values.facebook_url,
        instagram_handle: values.instagram_handle,
        linkedin_url: values.linkedin_url,
      });

      message.success('프로필이 성공적으로 업데이트되었습니다.');
      setEditMode(false);
      fetchProfile(); // Reload profile data
    } catch (error) {
      console.error('프로필 업데이트 실패:', error);
      message.error(error.message || '프로필 업데이트에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handleNotificationChange = async (field, value) => {
    try {
      await updateUserProfile({
        ...profileData.profile,
        [field]: value,
      });

      // Update local state
      setProfileData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [field]: value,
        }
      }));

      message.success('설정이 업데이트되었습니다.');
    } catch (error) {
      console.error('설정 업데이트 실패:', error);
      message.error(error.message || '설정 업데이트에 실패했습니다.');
    }
  };

  const handleGravatarToggle = async (checked) => {
    try {
      await updateUserProfile({
        ...profileData.profile,
        use_gravatar: checked,
      });

      // Update local state
      setProfileData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          use_gravatar: checked,
        }
      }));

      message.success('Gravatar 설정이 업데이트되었습니다.');
    } catch (error) {
      console.error('Gravatar 설정 업데이트 실패:', error);
      message.error(error.message || 'Gravatar 설정 업데이트에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="layout-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="layout-content">
        <Alert
          message="오류"
          description="프로필 데이터를 불러올 수 없습니다."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const { user, profile, portfolio_summary } = profileData;

  return (
    <>
      <style>
        {`
          .ant-breadcrumb {
            color: #fff !important;
          }
          .ant-breadcrumb a {
            color: rgba(255, 255, 255, 0.85) !important;
          }
          .ant-breadcrumb a:hover {
            color: #fff !important;
          }
          .ant-breadcrumb-separator {
            color: rgba(255, 255, 255, 0.65) !important;
          }
        `}
      </style>
      <div
        className="profile-nav-bg"
        style={{ backgroundImage: "url(" + BgProfile + ")" }}
      ></div>

      {/* Profile Header Card */}
      <Card
        className="card-profile-head"
        bodyStyle={{ display: "none" }}
        title={
          <Row justify="space-between" align="middle" gutter={[24, 0]}>
            <Col span={24} md={12} className="col-info">
              <Avatar.Group>
                <Avatar 
                  size={74} 
                  shape="square" 
                  src={profile.avatar_url}
                  icon={<UserOutlined />}
                />
                <div className="avatar-info">
                  <h4 className="font-semibold m-0">
                    {profile.full_name || user.username}
                  </h4>
                  <p>{user.email}</p>
                </div>
              </Avatar.Group>
            </Col>
            <Col
              span={24}
              md={12}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Space>
                {!editMode ? (
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    onClick={() => setEditMode(true)}
                  >
                    프로필 수정
                  </Button>
                ) : (
                  <>
                    <Button 
                      type="primary" 
                      icon={<SaveOutlined />}
                      onClick={() => form.submit()}
                      loading={updating}
                    >
                      저장
                    </Button>
                    <Button 
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setEditMode(false);
                        form.resetFields();
                      }}
                    >
                      취소
                    </Button>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        }
      />

      {/* Portfolio Summary Cards */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="총 자산"
              value={portfolio_summary?.total_assets || 0}
              precision={0}
              prefix="₩"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="활성 계좌"
              value={portfolio_summary?.active_accounts || 0}
              suffix="개"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="보유 종목"
              value={portfolio_summary?.holding_stocks || 0}
              suffix="개"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="이번 달 배당금"
              value={portfolio_summary?.monthly_dividends || 0}
              precision={0}
              prefix="₩"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        {/* Profile Information Card */}
        <Col xs={24} md={16}>
          <Card
            bordered={false}
            title={<h6 className="font-semibold m-0">프로필 정보</h6>}
            className="header-solid h-full"
          >
            {!editMode ? (
              <>
                {profile.bio && (
                  <>
                    <Paragraph style={{ marginBottom: 24 }}>
                      {profile.bio}
                    </Paragraph>
                    <Divider />
                  </>
                )}
                
                <Descriptions column={1}>
                  <Descriptions.Item 
                    label={<><UserOutlined /> 전체 이름</>}
                  >
                    {profile.full_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item 
                    label={<><MailOutlined /> 이메일</>}
                  >
                    {user.email}
                  </Descriptions.Item>
                  <Descriptions.Item 
                    label={<><PhoneOutlined /> 전화번호</>}
                  >
                    {profile.phone || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item 
                    label={<><EnvironmentOutlined /> 거주지</>}
                  >
                    {profile.location || '-'}
                  </Descriptions.Item>
                </Descriptions>

                {(profile.twitter_handle || profile.facebook_url || profile.instagram_handle || profile.linkedin_url) && (
                  <>
                    <Divider />
                    <div>
                      <Text strong>SNS 링크</Text>
                      <div style={{ marginTop: 12 }}>
                        <Space size="large">
                          {profile.twitter_handle && (
                            <a 
                              href={`https://twitter.com/${profile.twitter_handle}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <TwitterOutlined style={{ fontSize: 24, color: '#1DA1F2' }} />
                            </a>
                          )}
                          {profile.facebook_url && (
                            <a 
                              href={profile.facebook_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <FacebookOutlined style={{ fontSize: 24, color: '#4267B2' }} />
                            </a>
                          )}
                          {profile.instagram_handle && (
                            <a 
                              href={`https://instagram.com/${profile.instagram_handle}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <InstagramOutlined style={{ fontSize: 24, color: '#E1306C' }} />
                            </a>
                          )}
                          {profile.linkedin_url && (
                            <a 
                              href={profile.linkedin_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <LinkedinOutlined style={{ fontSize: 24, color: '#0077B5' }} />
                            </a>
                          )}
                        </Space>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleUpdateProfile}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="first_name"
                      label="이름"
                    >
                      <Input placeholder="이름" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="last_name"
                      label="성"
                    >
                      <Input placeholder="성" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="bio"
                  label="자기소개"
                >
                  <TextArea 
                    rows={4} 
                    placeholder="자신에 대해 소개해주세요" 
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="phone"
                      label="전화번호"
                    >
                      <Input placeholder="010-1234-5678" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="location"
                      label="거주지"
                    >
                      <Input placeholder="서울" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="gravatar_email"
                  label="Gravatar 이메일"
                  help="Gravatar에 등록된 이메일 주소를 입력하세요. 비워두면 계정 이메일을 사용합니다."
                >
                  <Input placeholder="your@email.com" />
                </Form.Item>

                <Divider>SNS 링크</Divider>

                <Form.Item
                  name="twitter_handle"
                  label="Twitter 핸들"
                >
                  <Input 
                    placeholder="username (@ 제외)" 
                    prefix={<TwitterOutlined />}
                  />
                </Form.Item>

                <Form.Item
                  name="facebook_url"
                  label="Facebook URL"
                >
                  <Input 
                    placeholder="https://facebook.com/yourprofile" 
                    prefix={<FacebookOutlined />}
                  />
                </Form.Item>

                <Form.Item
                  name="instagram_handle"
                  label="Instagram 핸들"
                >
                  <Input 
                    placeholder="username (@ 제외)" 
                    prefix={<InstagramOutlined />}
                  />
                </Form.Item>

                <Form.Item
                  name="linkedin_url"
                  label="LinkedIn URL"
                >
                  <Input 
                    placeholder="https://linkedin.com/in/yourprofile" 
                    prefix={<LinkedinOutlined />}
                  />
                </Form.Item>
              </Form>
            )}
          </Card>
        </Col>

        {/* Settings Card */}
        <Col xs={24} md={8}>
          <Card
            bordered={false}
            className="header-solid h-full"
            title={<h6 className="font-semibold m-0">설정</h6>}
          >
            <ul className="list settings-list">
              <li>
                <h6 className="list-header text-sm text-muted">아바타</h6>
              </li>
              <li>
                <Switch 
                  checked={profile.use_gravatar}
                  onChange={handleGravatarToggle}
                />
                <span>Gravatar 사용</span>
              </li>

              <li>
                <h6 className="list-header text-sm text-muted m-0">알림 설정</h6>
              </li>
              <li>
                <Switch 
                  checked={profile.email_notifications}
                  onChange={(checked) => handleNotificationChange('email_notifications', checked)}
                />
                <span>이메일 알림</span>
              </li>
              <li>
                <Switch 
                  checked={profile.trading_notifications}
                  onChange={(checked) => handleNotificationChange('trading_notifications', checked)}
                />
                <span>거래 알림</span>
              </li>
              <li>
                <Switch 
                  checked={profile.dividend_notifications}
                  onChange={(checked) => handleNotificationChange('dividend_notifications', checked)}
                />
                <span>배당금 알림</span>
              </li>
              <li>
                <Switch 
                  checked={profile.monthly_report}
                  onChange={(checked) => handleNotificationChange('monthly_report', checked)}
                />
                <span>월간 리포트</span>
              </li>
            </ul>

            <Divider />

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                마지막 거래: {portfolio_summary?.last_transaction_date || '-'}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Gravatar Info Card */}
      {profile.use_gravatar && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card>
              <Alert
                message="Gravatar 사용 중"
                description={
                  <div>
                    <p>
                      Gravatar는 이메일 주소를 기반으로 전역적으로 사용할 수 있는 아바타 서비스입니다.
                      <br />
                      현재 사용 중인 이메일: <strong>{profile.gravatar_email || user.email}</strong>
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      Gravatar에 이미지를 등록하려면{' '}
                      <a href="https://gravatar.com" target="_blank" rel="noopener noreferrer">
                        gravatar.com
                      </a>
                      을 방문하세요.
                    </p>
                  </div>
                }
                type="info"
                showIcon
              />
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}

export default Profile;
